import { StandardEvents } from '@shopify/events';

// Keep native product forms, subscription choices and cart events authoritative.
const configNode = document.getElementById('vechen-search-config');
const config = configNode ? JSON.parse(configNode.textContent) : null;
const widgets = new Set();
let sequence = 0;
let closingSearch = null;

async function closeSearchForCart() {
  widgets.forEach(widget => widget.close());
  const host = document.getElementById('search-modal');
  if (host?.querySelector('dialog[open]') && typeof host.closeDialog === 'function') {
    if (!closingSearch) closingSearch = Promise.resolve(host.closeDialog()).finally(() => { closingSearch = null; });
    await closingSearch;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  if (event.action !== 'add' || !document.querySelector('#cart-drawer cart-drawer-component[auto-open]')) return;
  // Do not dismiss a search or its error message after an unsuccessful addition.
  event.promise?.then(({ detail } = {}) => {
    if (!detail?.didError) return closeSearchForCart();
  }).catch(() => {});
});
document.addEventListener('theme-drawer:open', event => {
  if (event.target instanceof Element && event.target.closest('#cart-drawer')) closeSearchForCart();
}, true);
const cart = document.getElementById('cart-drawer');
if (cart) {
  new MutationObserver(() => {
    if (cart.hasAttribute('open') || cart.querySelector('dialog[open]')) closeSearchForCart();
  }).observe(cart, { attributes:true, subtree:true, attributeFilter:['open'] });
}

// Some autofill/paste input events have no inputType. The native component ignores
// those events; call its public handler after its own event listeners have run.
function repairNativeInput(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.name !== 'q' || event.isComposing) return;
  const native = input.closest('predictive-search-component');
  if (native && (!event.inputType || event.type === 'compositionend')) {
    queueMicrotask(() => native.search?.({ inputType:'insertText' }));
  }
}
document.addEventListener('input', repairNativeInput, true);
document.addEventListener('compositionend', repairNativeInput, true);

class InlineProductSearch {
  constructor(form, input) {
    this.form = form; this.input = input; this.cache = new Map(); this.index = -1;
    this.controller = null; this.timer = null;
    form.classList.add('vechen-has-prediction');
    this.box = document.createElement('div');
    this.box.className = 'vechen-inline-predictive'; this.box.hidden = true;
    this.box.id = `vechen-prediction-${++sequence}`;
    this.listId = `${this.box.id}-list`;
    form.append(this.box);
    input.autocomplete = 'off';
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-controls', this.listId);
    input.setAttribute('aria-expanded', 'false');
    input.addEventListener('input', event => {
      if (event.isComposing) return;
      this.queue();
    });
    input.addEventListener('compositionend', () => this.queue());
    input.addEventListener('focus', () => { if (input.value.trim()) this.queue(); });
    input.addEventListener('keydown', event => this.keydown(event), true);
    form.addEventListener('focusout', () => setTimeout(() => {
      if (!form.contains(document.activeElement)) this.close();
    }, 0));
  }
  queue() {
    clearTimeout(this.timer); this.controller?.abort(); this.index = -1;
    const query = this.input.value.trim();
    if (!query) { this.close(); return; }
    this.message(config.loading);
    this.timer = setTimeout(() => this.search(query), 200);
  }
  open() { this.box.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
  close() {
    clearTimeout(this.timer); this.controller?.abort(); this.index = -1;
    this.box.hidden = true; this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.input.removeAttribute('aria-busy');
  }
  message(text) {
    const p = document.createElement('p'); p.textContent = text; p.setAttribute('role','status');
    const list = document.createElement('ul'); list.id = this.listId; list.setAttribute('role','listbox');
    list.setAttribute('aria-label',config.resultsLabel);
    this.box.replaceChildren(p, list); this.open();
  }
  async search(query) {
    const controller = new AbortController(); this.controller = controller;
    this.input.setAttribute('aria-busy','true');
    try {
      let html = this.cache.get(query);
      if (html === undefined) {
        const url = new URL(config.suggestUrl, location.origin);
        url.searchParams.set('q',query);
        url.searchParams.set('section_id','predictive-search');
        url.searchParams.set('resources[type]','product');
        url.searchParams.set('resources[limit_scope]','each');
        url.searchParams.set('resources[limit]','10');
        const response = await fetch(url, { signal:controller.signal, credentials:'same-origin' });
        if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
        html = await response.text();
        if (this.cache.size >= 50) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query,html);
      }
      if (controller.signal.aborted || this.input.value.trim() !== query) return;
      const documentFragment = new DOMParser().parseFromString(html,'text/html');
      if (!documentFragment.querySelector('#predictive-search-results')) throw new Error('Missing search response');
      const cards = [...documentFragment.querySelectorAll('.predictive-search-results__card--product')];
      this.render(cards, query);
    } catch (error) {
      if (error.name !== 'AbortError' && !controller.signal.aborted) this.message(config.error);
    } finally {
      if (this.controller === controller) this.input.removeAttribute('aria-busy');
    }
  }
  render(cards, query) {
    const list = document.createElement('ul'); list.id = this.listId;
    list.setAttribute('role','listbox'); list.setAttribute('aria-label',config.resultsLabel);
    const seen = new Set();
    cards.slice(0,8).forEach(card => {
      const source = card.querySelector('a[href*="/products/"]');
      if (!source) return;
      const url = new URL(source.getAttribute('href'), location.origin);
      if (url.origin !== location.origin || seen.has(url.pathname)) return;
      seen.add(url.pathname);
      const item = document.createElement('li'); item.id = `${this.listId}-${seen.size}`;
      item.setAttribute('role','option'); item.setAttribute('aria-selected','false');
      const link = document.createElement('a'); link.href = url.href; link.tabIndex = -1;
      const sourceImage = card.querySelector('img');
      if (sourceImage) {
        const image = sourceImage.cloneNode(); image.alt = ''; image.width = 52; image.height = 60;
        image.removeAttribute('srcset'); image.removeAttribute('sizes'); image.loading = 'lazy'; link.append(image);
      }
      const copy = document.createElement('span'); const title = document.createElement('strong');
      title.textContent = card.querySelector('.resource-card__title')?.textContent.trim() || source.getAttribute('aria-label') || source.textContent.trim();
      copy.append(title);
      const priceText = card.querySelector('product-price, .price, .resource-card__price')?.textContent.trim();
      if (priceText) { const price = document.createElement('small'); price.textContent = priceText; copy.append(price); }
      link.append(copy); item.append(link); list.append(item);
    });
    this.box.replaceChildren(list);
    if (!list.children.length) {
      const empty = document.createElement('p'); empty.setAttribute('role','status');
      empty.textContent = config.empty.replace('[query]',query); this.box.prepend(empty);
    }
    const all = document.createElement('a'); const destination = new URL(config.searchUrl,location.origin);
    destination.searchParams.set('q',query); destination.searchParams.set('type','product,page,article');
    all.href = destination.href; all.className = 'vechen-predictive-all'; all.textContent = config.viewAll;
    this.box.append(all); this.index = -1; this.input.removeAttribute('aria-activedescendant'); this.open();
  }
  keydown(event) {
    if (this.box.hidden) return;
    const items = [...this.box.querySelectorAll('[role="option"]')];
    if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); this.close(); return; }
    if (event.key === 'Enter' && this.index >= 0) {
      event.preventDefault(); event.stopImmediatePropagation(); items[this.index]?.querySelector('a')?.click(); return;
    }
    if (!items.length || !['ArrowDown','ArrowUp'].includes(event.key)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    this.index = event.key === 'ArrowDown' ? (this.index + 1) % items.length : (this.index <= 0 ? items.length - 1 : this.index - 1);
    items.forEach((item,i) => item.setAttribute('aria-selected',String(i === this.index)));
    const active = items[this.index]; this.input.setAttribute('aria-activedescendant',active.id);
    active.scrollIntoView({block:'nearest'});
  }
}

function install(scope = document) {
  if (!config) return;
  scope.querySelectorAll('form input[name="q"]').forEach(input => {
    const form = input.form;
    if (!form || form.dataset.vechenPredictionReady || input.closest('predictive-search-component')) return;
    let url; try { url = new URL(form.action,location.origin); } catch { return; }
    if (url.origin !== location.origin || url.pathname !== new URL(config.searchUrl,location.origin).pathname) return;
    form.dataset.vechenPredictionReady = 'true'; widgets.add(new InlineProductSearch(form,input));
  });
}
document.addEventListener('pointerdown', event => widgets.forEach(widget => {
  if (!widget.form.contains(event.target)) widget.close();
}));
document.addEventListener('shopify:section:load', event => install(event.target));
document.addEventListener('shopify:section:unload', event => widgets.forEach(widget => {
  if (event.target.contains(widget.form)) { widget.close(); widgets.delete(widget); }
}));
install();
